import { type AnalyticsDateRangeParam, axiosCacheInterceptorRequestConfig } from '../analyticsClient.types';
import type {
    ClickThroughRateAnalyticsParams,
    OutboundMessagesParams,
    OutboundMessageFilters,
    OutboundMessagesAnalyticsClientInterface,
} from './outboundMessagesAnalyticsClient.types';
import type { BaseClientConfig, ExtendedAxiosRequestConfig } from '~/services/BaseClient';
import BaseClient from '../../BaseClient';

export default class GroupOutboundMessagesAnalyticsClient
    extends BaseClient
    implements OutboundMessagesAnalyticsClientInterface
{
    constructor(private teamId: number, private groupId: string, config: BaseClientConfig = {}) {
        super(config);
    }

    getTotalMessagesSentByAudienceType(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/total-messages-sent-by-audience-type`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getTotalMessagesSentByResource(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/total-messages-sent-by-resource`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getOptOutRate(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/opt-out-rate`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getClickThroughRate(params: ClickThroughRateAnalyticsParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/click-through-rate`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getUniqueClickThroughRate(params: ClickThroughRateAnalyticsParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/unique-click-through-rate`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getMessageReplyRate(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/message-reply-rate`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getListActiveResources(
        params: AnalyticsDateRangeParam & Pick<OutboundMessageFilters['filters'], 'resources'>,
        config?: ExtendedAxiosRequestConfig
    ) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/list-active-resources`;
        return this.makeCoreApiRequest(url, 'post', params, null, {
            ...axiosCacheInterceptorRequestConfig,
            ...config,
        });
    }

    getTotalActiveResourcesByResourceType(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/total-active-resources-by-resource-type`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getTotalMessagesSentByMessageType(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/total-messages-sent-by-message-type`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getDeliveryRate(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/delivery-rate`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getTotalContactsMessaged(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/total-contacts-messaged`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getClickThroughRateByResource(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/click-through-rate-by-resource`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getUniqueClickThroughRateByResource(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/unique-click-through-rate-by-resource`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getReplyRateByResource(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/message-reply-rate-by-resource`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getOptOutRateByResource(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/opt-out-rate-by-resource`;
        return this.makeCoreApiRequest(url, 'post', params, null, axiosCacheInterceptorRequestConfig);
    }

    getExportAudienceOriginMetrics(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/export-audience-origin-metrics`;
        return this.makeCoreApiRequest(url, 'post', params, null);
    }

    getExportResourceMetrics(params: OutboundMessagesParams) {
        const url = `${this.apiBase}/teams/${this.teamId}/groups/${this.groupId}/statistics/time-series/outbound-messages/export-resource-metrics`;
        return this.makeCoreApiRequest(url, 'post', params, null);
    }
}
